import { useCallback, useEffect, useMemo, useState } from "react";
import BinScanner from "@/components/feature/BinScanner";
import OpsHeader from "@/components/feature/OpsHeader";
import ProductScanner from "@/components/feature/ProductScanner";
import { useAuth } from "@/hooks/useAuth";
import { useOpsWarehouse } from "@/hooks/useOpsWarehouse";
import {
  errorText,
  opsApi,
  qtyText,
  type PickItem,
  type PickTask,
} from "@/lib/warehouseOps";

/**
 * Lấy hàng cho các phiếu xuất đã duyệt.
 *
 * Phiếu đã chỉ đích danh lô nào nằm ở ô nào, nên máy cầm tay bắt quét đúng ô đó
 * mới cho đánh dấu đã lấy — lấy lô khác ra thì giá vốn và hạn dùng lệch khỏi
 * chứng từ, và không ai phát hiện cho tới khi kiểm kê.
 *
 * Mọi loại phiếu xuất đều hiện ở đây (bán hàng, biếu tặng, hàng mẫu, bảo hành…).
 * Với người đứng trong kho thì cả mười mấy loại đều là một việc: lấy đúng hàng
 * ở đúng ô rồi đưa đi.
 *
 * Mỗi dòng phải quét đủ hai thứ theo thứ tự: ô trước, rồi mã hàng. Ô chứa được
 * nhiều mặt hàng, nên đứng đúng ô vẫn có thể với nhầm thùng bên cạnh.
 */
export default function OpsPickPage() {
  const warehouse = useOpsWarehouse();
  const { getToken } = useAuth();
  const token = getToken() ?? undefined;

  const [tasks, setTasks] = useState<PickTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");
  const [warning, setWarning] = useState("");
  const [search, setSearch] = useState("");

  const [openId, setOpenId] = useState("");
  /** Dòng đang xác nhận, và mã ô đã quét xong của dòng đó. */
  const [scanning, setScanning] = useState<PickItem | null>(null);
  const [scannedBinCode, setScannedBinCode] = useState("");
  const [issuing, setIssuing] = useState(false);

  const load = useCallback(async () => {
    if (!warehouse) return;

    setLoading(true);
    setError("");

    try {
      const res = await opsApi.pickTasks(warehouse.id, token);
      setTasks(res.data ?? []);
    } catch (e) {
      setError(errorText(e, "Không tải được danh sách phiếu xuất."));
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [warehouse, token]);

  useEffect(() => { void load(); }, [load]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tasks;

    return tasks.filter((t) =>
      [t.code, t.partner, t.typeLabel].some((v) => (v ?? "").toLowerCase().includes(q)));
  }, [tasks, search]);

  const current = useMemo(() => tasks.find((t) => t.id === openId) ?? null, [tasks, openId]);

  const pick = async (productCode: string) => {
    if (!current || !scanning || !scannedBinCode) return;

    setError("");
    setWarning("");

    try {
      const res = await opsApi.pickItem(
        current.id,
        scanning.id,
        scannedBinCode,
        productCode,
        token,
      );

      setTasks((prev) => prev.map((t) => (t.id === res.data.id ? res.data : t)));
      setFlash(res.message);
      setWarning(res.warning ?? "");
      setScanning(null);
      setScannedBinCode("");
    } catch (e) {
      setError(errorText(e, "Không xác nhận được dòng hàng."));
    }
  };

  const issue = async () => {
    if (!current) return;

    setIssuing(true);
    setError("");
    setWarning("");

    try {
      const res = await opsApi.issuePickTask(current.id, token);
      setFlash(res.message || `Đã xuất kho phiếu ${current.code}.`);
      setOpenId("");
      await load();
    } catch (e) {
      setError(errorText(e, "Không xuất kho được."));
    } finally {
      setIssuing(false);
    }
  };

  if (!warehouse) return null;

  // ─── Quét ô cho một dòng hàng ─────────────────────────────────────────────
  if (current && scanning) {
    return (
      <div className="min-h-screen bg-slate-50">
        <OpsHeader
          title={scannedBinCode ? "Quét mã hàng" : "Quét ô để lấy hàng"}
          subtitle={`${current.code} · bước ${scannedBinCode ? 2 : 1} / 2`}
          warehouseName={warehouse.name}
          onBack={() => { setScanning(null); setScannedBinCode(""); setError(""); }}
        />

        <div className="p-4 space-y-3">
          <div className="bg-white rounded-xl border border-slate-200 p-3">
            <p className="font-semibold text-slate-900">{scanning.productName}</p>
            <p className="text-xs text-slate-500 font-mono mt-0.5">
              {scanning.productCode}
              {scanning.lotNumber ? ` · Lô ${scanning.lotNumber}` : ""}
              {scanning.expiryDate ? ` · HSD ${scanning.expiryDate}` : ""}
            </p>
            <p className="text-sm text-slate-700 mt-2">
              Cần lấy <strong>{qtyText(scanning.requestedQty)} {scanning.unit}</strong>
            </p>
          </div>

          {!scannedBinCode ? (
            <BinScanner
              warehouseId={warehouse.id}
              token={token}
              label="Quét mã ô ghi trên phiếu"
              expectedCode={scanning.binCode || undefined}
              onScanned={(bin) => { setScannedBinCode(bin.code); setError(""); }}
            />
          ) : (
            <>
              <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3">
                <p className="text-xs text-emerald-700">Đã quét ô</p>
                <p className="font-mono text-sm text-emerald-900 break-all">{scannedBinCode}</p>
                <button
                  onClick={() => { setScannedBinCode(""); setError(""); }}
                  className="mt-1.5 text-sm font-medium text-emerald-700"
                >
                  Quét lại ô
                </button>
              </div>

              <ProductScanner
                token={token}
                label="Quét mã hàng trên thùng vừa lấy"
                hint="Quét đúng mặt hàng của dòng này thì máy tự ghi nhận đã lấy."
                expected={[scanning.productCode]}
                expectedLabel={`Dòng này cần lấy ${scanning.productCode}.`}
                onScanned={(p) => void pick(p.code)}
              />
            </>
          )}

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}
        </div>
      </div>
    );
  }

  // ─── Các dòng của một phiếu ───────────────────────────────────────────────
  if (current) {
    const done = current.pickedItems >= current.totalItems && current.totalItems > 0;

    return (
      <div className="min-h-screen bg-slate-50 pb-28">
        <OpsHeader
          title={current.code}
          subtitle={`${current.typeLabel}${current.partner ? ` · ${current.partner}` : ""}`}
          warehouseName={warehouse.name}
          onBack={() => { setOpenId(""); setFlash(""); setWarning(""); setError(""); }}
        />

        <div className="px-4 pt-3 space-y-2">
          <p className="text-xs text-slate-500">
            Đã lấy {current.pickedItems}/{current.totalItems} dòng &middot; ngày phiếu {current.issueDate || "—"}
          </p>

          {flash && (
            <p className="text-sm text-brand-700 bg-brand-50 border border-brand-200 rounded-lg px-3 py-2">{flash}</p>
          )}
          {warning && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              ⚠ {warning}
            </p>
          )}
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}
          {!current.supported && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Loại phiếu này phải xuất trên máy tính; ở đây chỉ lấy hàng được thôi.
            </p>
          )}
        </div>

        <div className="p-4 space-y-2">
          {current.items.map((it) => {
            const picked = it.status === "picked" || it.status === "issued";

            return (
              <button
                key={it.id}
                onClick={() => { if (!picked) { setScanning(it); setScannedBinCode(""); setError(""); } }}
                disabled={picked}
                className={`w-full text-left rounded-xl border p-3 ${
                  picked ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white active:bg-slate-50"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-slate-900 text-sm truncate">{it.productName}</p>
                  <span className="shrink-0 text-xs text-slate-600">
                    {qtyText(it.requestedQty)} {it.unit}
                  </span>
                </div>
                <p className="text-xs text-slate-500 font-mono mt-0.5">
                  {it.productCode}
                  {it.lotNumber ? ` · Lô ${it.lotNumber}` : ""}
                </p>
                <div className="flex items-center justify-between gap-2 mt-1.5">
                  <span className="text-[11px] font-mono text-slate-600 truncate">
                    Ô {it.binCode || "chưa gán"}
                  </span>
                  <span className={`text-[11px] font-medium shrink-0 ${picked ? "text-emerald-700" : "text-brand-700"}`}>
                    {picked ? "✓ Đã lấy" : "Quét ô + mã hàng →"}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-3">
          <button
            onClick={() => void issue()}
            disabled={!done || issuing || !current.supported}
            className={`w-full py-3.5 rounded-xl font-semibold text-base text-white ${
              !done || issuing || !current.supported ? "bg-slate-300" : "bg-brand-600"
            }`}
          >
            {issuing
              ? "Đang xuất kho..."
              : done
                ? "Xuất kho phiếu này"
                : `Còn ${current.totalItems - current.pickedItems} dòng chưa quét`}
          </button>
        </div>
      </div>
    );
  }

  // ─── Danh sách phiếu chờ lấy hàng ─────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 pb-6">
      <OpsHeader title="Lấy hàng để xuất" warehouseName={warehouse.name} />

      <div className="p-4 space-y-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Tìm mã phiếu, khách hàng..."
          className="w-full px-3 py-2.5 text-sm border-2 border-slate-200 rounded-lg focus:outline-none focus:border-brand-500"
        />

        {flash && (
          <p className="text-sm text-brand-700 bg-brand-50 border border-brand-200 rounded-lg px-3 py-2">{flash}</p>
        )}
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <span className="flex-1">{error}</span>
            <button onClick={() => void load()} className="font-medium text-red-700">Tải lại</button>
          </div>
        )}

        {loading ? (
          <p className="text-center text-slate-400 py-12">Đang tải...</p>
        ) : visible.length === 0 ? (
          <p className="text-center text-slate-400 py-12">
            Kho này không có phiếu xuất nào đã duyệt đang chờ lấy hàng.
          </p>
        ) : (
          visible.map((t) => (
            <button
              key={t.id}
              onClick={() => { setOpenId(t.id); setFlash(""); setWarning(""); setError(""); }}
              className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 active:bg-slate-50"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="font-semibold text-slate-900 font-mono text-sm">{t.code}</p>
                <span className="shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                  {t.typeLabel}
                </span>
              </div>
              {t.partner && <p className="text-sm text-slate-600 mt-1 truncate">{t.partner}</p>}
              <div className="flex items-center justify-between gap-2 mt-1">
                <span className="text-xs text-slate-400">{t.issueDate || "—"}</span>
                <span
                  className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                    t.pickedItems >= t.totalItems
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-amber-50 text-amber-700"
                  }`}
                >
                  {t.pickedItems}/{t.totalItems} dòng đã lấy
                </span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
